// Layout of a dashboard typology (SPEC-0052 v1 C2, SPEC-0060 §4.2): a grid of panels, each with
// its own default geometry and limits. The Resource & capacity tab is the default typology: 6
// columns, each panel 3..6 columns wide and 2..5 rows high, stacked in the order below.

export const COLUMNS = 6;
export const LEGACY_PERSISTENCE_KEY = "metrics_portfolio_dashboard_v6";

export const PANEL_IDS = [
  "ProjectPortfolioUsage",
  "ProjectPortfolioStatus",
  "ProjectPortfolioDemandVsCapacity",
  "ProjectAnnualBudget",
  "ProjectTeamAvailability",
  "PortfolioYearlyBudgetUsage",
];

export const PANEL_LIMITS = { minSizeX: 3, minSizeY: 2, maxSizeX: 6, maxSizeY: 5 };

export const DEFAULT_LAYOUT = PANEL_IDS.map((id, i) => ({
  id,
  row: i * 2,
  col: 0,
  sizeX: 6,
  sizeY: 2,
}));

/** Panel definitions of the Resource & capacity typology: default geometry plus limits. */
export const RESOURCE_CAPACITY_PANELS = DEFAULT_LAYOUT.map((p) => ({ ...p, ...PANEL_LIMITS }));

const isInt = (n) => Number.isInteger(n);

const validPanel = (p, def, columns) =>
  p &&
  isInt(p.row) && p.row >= 0 &&
  isInt(p.col) && p.col >= 0 &&
  isInt(p.sizeX) && p.sizeX >= def.minSizeX && p.sizeX <= def.maxSizeX &&
  isInt(p.sizeY) && p.sizeY >= def.minSizeY && p.sizeY <= def.maxSizeY &&
  p.col + p.sizeX <= columns;

const geometry = (p) => ({ id: p.id, row: p.row, col: p.col, sizeX: p.sizeX, sizeY: p.sizeY });

/**
 * The panels of a typology with the saved geometry where it is valid and the default otherwise. A
 * saved document is never trusted: unknown ids are dropped, malformed panels fall back to their
 * default.
 * @param {Array} saved the saved geometry list
 * @param {Array} panelDefs [{ id, row, col, sizeX, sizeY, minSizeX, minSizeY, maxSizeX, maxSizeY }]
 * @param {number} columns the grid's column count
 */
export const sanitizeLayoutFor = (saved, panelDefs, columns) => {
  const byId = new Map((Array.isArray(saved) ? saved : []).filter((p) => p && p.id).map((p) => [p.id, p]));
  return panelDefs.map((d) => {
    const p = byId.get(d.id);
    return validPanel(p, d, columns) ? geometry(p) : geometry(d);
  });
};

/** Resource & capacity: the six panels, saved geometry where valid, default otherwise. */
export const sanitizeLayout = (saved) => sanitizeLayoutFor(saved, RESOURCE_CAPACITY_PANELS, COLUMNS);

/**
 * A saved geometry list kept as data only (integers, known shape), for a typology whose panels are
 * only known at render time (the project's Monitoring metrics: EVM panels depend on the data). The
 * page applies it with `sanitizeLayoutFor` once its panels are known.
 */
export const sanitizeGeometry = (saved) =>
  (Array.isArray(saved) ? saved : [])
    .filter(
      (p) =>
        p && typeof p.id === "string" && p.id.length > 0 && p.id.length <= 64 &&
        [p.row, p.col, p.sizeX, p.sizeY].every((n) => isInt(n) && n >= 0)
    )
    .map(geometry);

/** Syncfusion's serialize() output reduced to what is saved. */
export const toSavedLayout = (serialized, panelDefs = RESOURCE_CAPACITY_PANELS, columns = COLUMNS) =>
  sanitizeLayoutFor(
    (serialized || []).map((p) => ({
      id: p.id,
      row: Number(p.row),
      col: Number(p.col),
      sizeX: Number(p.sizeX),
      sizeY: Number(p.sizeY),
    })),
    panelDefs,
    columns
  );

export const sameLayout = (a, b) =>
  JSON.stringify(sanitizeLayout(a)) === JSON.stringify(sanitizeLayout(b));

const legacyStorageKey = (gridId) => "DashboardLayout" + gridId;

/**
 * The layout a browser kept under Syncfusion's persistence key before it moved to the server, or
 * null. Read once, on a first visit; `forgetLegacyLayout` then removes it so it is never read again.
 */
export const readLegacyLayout = (gridId = LEGACY_PERSISTENCE_KEY) => {
  try {
    const raw = localStorage.getItem(legacyStorageKey(gridId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || !Array.isArray(parsed.panels)) return null;
    return gridId === LEGACY_PERSISTENCE_KEY ? sanitizeLayout(parsed.panels) : sanitizeGeometry(parsed.panels);
  } catch (_) {
    return null;
  }
};

export const forgetLegacyLayout = (gridId) => {
  try {
    localStorage.removeItem(legacyStorageKey(gridId));
  } catch (_) {
    // Nothing kept: nothing to remove.
  }
};

/**
 * The hidden panels of a saved document: known ids only, and never all of them — at least one
 * panel stays visible (SPEC-0052 v1, C1).
 */
export const sanitizeHiddenFor = (hidden, panelIds) => {
  const known = (Array.isArray(hidden) ? hidden : []).filter((id) => panelIds.includes(id));
  const unique = panelIds.filter((id) => known.includes(id));
  return unique.length >= panelIds.length ? [] : unique;
};

export const sanitizeHidden = (hidden) => sanitizeHiddenFor(hidden, PANEL_IDS);

export const visiblePanelIdsFor = (hidden, panelIds) => panelIds.filter((id) => !(hidden || []).includes(id));

export const visiblePanelIds = (hidden) => visiblePanelIdsFor(hidden, PANEL_IDS);
